import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { SERVER } from '../config/config';
import ServerImage from './ServerImage';
import { FaChevronRight, FaPlay, FaPause } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from "react-icons/fa6";
import { FaArrowLeft } from "react-icons/fa6";


const AboutTestimonialsCarousel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Reset audio when testimonial changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load(); // Reload the audio element with new source
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [currentIndex]);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${SERVER}/testimonials?type=carousel`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      // Fallback data
      setTestimonials([
        {
          id: 1,
          client_name: 'Mr Fathi',
          title: 'Fournitest founder',
          review: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse volutpat pellentesque semper. Pellentesque et sollicitudin sapien. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse volutpat, pellentesque semper. Pellentesque et sollicitudin sapien.',
          photo: '/testimonials/fathi.png',
          long_image: '/testimonials/fathi-long.png',
          audio: null, // Set to null since we don't have actual audio files
          date: 'September 2025'
        },
        {
          id: 2,
          client_name: 'Rabeb Michkette',
          title: 'Founder michkette',
          review: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse volutpat pellentesque semper. Pellentesque et sollicitudin sapien.',
          photo: '/testimonials/rabeb.png',
          long_image: '/testimonials/rabeb-long.png',
          audio: null, // Set to null since we don't have actual audio files
          date: 'August 2025'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Format date to abbreviated format (e.g., "Sep, 2025")
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Try to parse the date string
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        // If not a valid date, try to extract month and year from string like "September 2025"
        const parts = dateString.split(' ');
        if (parts.length >= 2) {
          const monthMap = {
            'january': 'Jan', 'february': 'Feb', 'march': 'Mar', 'april': 'Apr',
            'may': 'May', 'june': 'Jun', 'july': 'Jul', 'august': 'Aug',
            'september': 'Sep', 'october': 'Oct', 'november': 'Nov', 'december': 'Dec'
          };
          const month = monthMap[parts[0].toLowerCase()] || parts[0].substring(0, 3);
          const year = parts[1];
          return `${month}, ${year}`;
        }
        return dateString;
      }
      
      // Format as "Mon, YYYY"
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[date.getMonth()]}, ${date.getFullYear()}`;
    } catch (error) {
      return dateString;
    }
  };

  const nextTestimonial = () => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const prevTestimonial = () => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        // Check if audio source exists before playing
        if (currentTestimonial.audio) {
          audioRef.current.play().catch(error => {
            console.error('Error playing audio:', error);
            setIsPlaying(false);
          });
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleGetStarted = () => {
    navigate('/book-consultation');
  };

  if (loading) {
    return (
      <section className="py-20 px-4 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-12 md:py-20 px-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 md:mb-16">
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-8">
          Testimonials
        </h2>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        {/* Main Testimonial Card - Single card with shadow */}
        <div className="bg-white  rounded-3xl shadow-lg overflow-hidden mb-8">
          <div className="flex">
            {/* Left - Image (40% width) */}
            <div className="w-[40%] flex-shrink-0 rounded-3xl">
              <ServerImage
                src={currentTestimonial.long_image || currentTestimonial.photo}
                alt={currentTestimonial.client_name}
                fallback="/testimonials/fathi.png"
                className="w-full h-full object-cover "
              />
            </div>

            {/* Right - Content (60% width) */}
            <div className="w-[60%] p-4 flex flex-col relative">
              {/* Comma Image - Top Right Corner */}
              <img 
                src="/comma.png" 
                alt="" 
                className="absolute top-2 right-2 w-12 h-12 pointer-events-none z-10"
                style={{ opacity: 0.1 }}
              />
              
              {/* Name and Title */}
              <div className="mb-3">
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {currentTestimonial.client_name}
                </h3>
                <p className="text-gray-400 text-xs">
                  {currentTestimonial.title}
                </p>
              </div>

              {/* Testimonial Text */}
              <p className="text-gray-700 leading-relaxed text-xs mb-3 flex-1">
                {currentTestimonial.review}
              </p>

              {/* Date - Right aligned */}
              <p className="text-gray-400 text-xs text-right mb-3">
                {formatDate(currentTestimonial.date)}
              </p>

              {/* Gray Divider Line */}
              <div className="border-t border-gray-200 mb-3"></div>

              {/* Audio Player */}
              {currentTestimonial.audio && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleAudio}
                    className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors duration-300 shadow-md flex-shrink-0"
                    style={{ backgroundColor: 'var(--primary-blue)' }}
                  >
                    {isPlaying ? (
                      <FaPause className="text-white text-xs" />
                    ) : (
                      <FaPlay className="text-white text-xs ml-0.5" />
                    )}
                  </button>

                  {/* Audio Waveform Visualization */}
                  <div className="flex-1 flex items-center gap-0.5 h-8">
                    {[...Array(15)].map((_, index) => {
                      const heights = [3, 5, 4, 7, 5, 6, 3, 8, 4, 5, 7, 4, 6, 3, 7];
                      const isActive = (currentTime / duration) * 15 > index;
                      return (
                        <div
                          key={index}
                          className={`w-1 rounded-full transition-all duration-200`}
                          style={{ 
                            height: `${heights[index] * 2.5}px`,
                            backgroundColor: isActive ? 'var(--primary-blue)' : '#D1D5DB'
                          }}
                        />
                      );
                    })}
                  </div>

                  {/* Hidden Audio Element */}
                  <audio
                    ref={audioRef}
                    src={currentTestimonial.audio ? `${SERVER}${currentTestimonial.audio}` : undefined}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                    onError={(e) => {
                      console.error('Audio error:', e);
                      setIsPlaying(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <div className="flex justify-center gap-8 mb-8">
          <button
            onClick={prevTestimonial}
            className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <FaArrowLeft className="text-lg" style={{ color: 'var(--primary-blue)' }} />
          </button>
          <button
            onClick={nextTestimonial}
            className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <FaArrowRight className="text-lg" style={{ color: 'var(--primary-blue)' }} />
          </button>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-8"></div>

        {/* Call to Action */}
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-6">
            Let's get your project moving
          </h3>
          <button
            onClick={handleGetStarted}
            className="inline-flex items-center gap-3 text-white px-6 py-3 rounded-full font-medium text-base hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 group"
            style={{ backgroundColor: 'var(--primary-blue)' }}
          >
            <span>Get started</span>
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
              <FaChevronRight className="text-blue-600 text-sm" />
            </div>
          </button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:block">
        {/* Testimonial Carousel */}
        <div className="flex items-center gap-8 mb-16">
        {/* Left Navigation */}
        <button
          onClick={prevTestimonial}
          className="w-32 h-80 rounded-3xl relative overflow-hidden group transition-all duration-300 hover:scale-105"
          style={{ borderRadius: '28px' }}
        >
          {/* Previous testimonial image */}
          <ServerImage
            src={testimonials[(currentIndex - 1 + testimonials.length) % testimonials.length]?.long_image || 
                 testimonials[(currentIndex - 1 + testimonials.length) % testimonials.length]?.photo}
            alt={testimonials[(currentIndex - 1 + testimonials.length) % testimonials.length]?.client_name}
            fallback="/testimonials/fathi.png"
            className="w-full h-80 object-cover"
          />
          {/* Blue overlay */}
          <div 
            className="absolute inset-0 bg-opacity-70 flex items-center justify-center transition-all duration-300 group-hover:bg-opacity-50"
            style={{ backgroundColor: 'var(--primary-blue)' }}
          >
            <FaArrowLeft 
              size={40} 
              className="p-2 rounded-full text-2xl"
              style={{ color: 'var(--primary-blue)', backgroundColor: 'white' }}
            />
          </div>
        </button>

        {/* Main Testimonial Card */}
        <div className="flex-1 flex gap-8 items-center">
          {/* Long Image */}
          <div className="w-60 h-80 rounded-3xl overflow-hidden" style={{ borderRadius: '28px' }}>
            <ServerImage
              src={currentTestimonial.long_image || currentTestimonial.photo}
              alt={currentTestimonial.client_name}
              fallback="/testimonials/fathi.png"
              className="w-full h-80 object-cover"
            />
          </div>

          {/* Content */}
          <div className="flex-1 space-y-4 shadow-xl rounded-3xl h-80 p-6 relative">
            {/* Comma Image - Top Right Corner */}
            <img 
              src="/comma.png" 
              alt="" 
              className="absolute top-4 right-4 w-24 h-24 pointer-events-none"
              style={{ opacity: 0.1 }}
            />
            
            {/* Name and Title */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {currentTestimonial.client_name}
              </h3>
              <p className="text-gray-400 text-xl">
                {currentTestimonial.title}
              </p>
            </div>

            {/* Testimonial Text */}
            <p className="text-gray-700 leading-relaxed text-base">
              {currentTestimonial.review}
            </p>

            {/* Divider Line */}
            <div className="border-b border-gray-200"></div>

            {/* Audio Player */}
            {currentTestimonial.audio && (
              <div className="flex items-center gap-4">
                <button
                  onClick={toggleAudio}
                  className="w-10 h-10 bg-primary-blue rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors duration-300 shadow-lg"
                >
                  {isPlaying ? (
                    <FaPause className="text-white text-lg" />
                  ) : (
                    <FaPlay className="text-white text-lg ml-1" />
                  )}
                </button>

                {/* Audio Waveform Visualization */}
                <div className="flex-1 flex items-center gap-1 h-12">
                  {[...Array(25)].map((_, index) => {
                    const heights = [3, 6, 4, 8, 5, 7, 3, 9, 4, 6, 8, 5, 7, 4, 6, 3, 8, 5, 7, 6, 4, 8, 5, 7, 3];
                    const isActive = (currentTime / duration) * 25 > index;
                    return (
                      <div
                        key={index}
                        className={`w-1 rounded-full transition-all duration-200 ${
                          isActive
                            ? 'bg-primary-blue'
                            : 'bg-gray-300'
                        }`}
                        style={{ height: `${heights[index] * 4}px` }}
                      />
                    );
                  })}
                </div>

                {/* Date */}
                <span className="text-gray-400 text-lg font-medium">
                  {formatDate(currentTestimonial.date)}
                </span>

                {/* Hidden Audio Element */}
                <audio
                  ref={audioRef}
                  src={currentTestimonial.audio ? `${SERVER}${currentTestimonial.audio}` : undefined}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                  onError={(e) => {
                    console.error('Audio error:', e);
                    setIsPlaying(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Navigation */}
        <button
          onClick={nextTestimonial}
          className="w-32 h-80 rounded-3xl relative overflow-hidden group transition-all duration-300 hover:scale-105"
          style={{ borderRadius: '28px' }}
        >
          {/* Next testimonial image */}
          <ServerImage
            src={testimonials[(currentIndex + 1) % testimonials.length]?.long_image || 
                 testimonials[(currentIndex + 1) % testimonials.length]?.photo}
            alt={testimonials[(currentIndex + 1) % testimonials.length]?.client_name}
            fallback="/testimonials/fathi.png"
            className="w-full h-80 object-cover"
          />
          {/* Blue overlay */}
          <div 
            className="absolute inset-0 bg-opacity-70 flex items-center justify-center transition-all duration-300 group-hover:bg-opacity-50"
            style={{ backgroundColor: 'var(--primary-blue)' }}
          >
            <FaArrowRight 
              size={40} 
              className="p-2 rounded-full text-2xl"
              style={{ color: 'var(--primary-blue)', backgroundColor: 'white' }}
            />
          </div>
        </button>
      </div>
      <div className='border-b border mb-8'></div>

      {/* Call to Action */}
      <div className="text-center">
        <h3 className="text-3xl font-bold text-gray-900 mb-8">
          Let's get your project moving
        </h3>
        <button
          onClick={handleGetStarted}
          className="inline-flex items-center gap-3 bg-primary-blue text-white px-4 md:px-8 py-2 md:py-4 rounded-full font-medium text-lg hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 group"
        >
          <span>Get started</span>
          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
            <FaChevronRight className="text-blue-600 text-sm" />
          </div>
        </button>
      </div>
      </div>
    </section>
  );
};

export default AboutTestimonialsCarousel;